import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, postWithRetry } from "../lib/api";
import { toast } from "sonner";
import BrandHeader from "../components/BrandHeader";
import SessionFormBody from "../components/SessionFormBody";
import { Button } from "../components/ui/button";
import { CheckCircle2, Loader2 } from "lucide-react";
import {
  emptyForm, isFormComplete, toApiPayload,
} from "../lib/sessionForm";

export default function TeacherForm() {
  const [schools, setSchools] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [savedThisSession, setSavedThisSession] = useState(0);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    api.get("/schools").then((r) => setSchools(r.data)).catch(() => setSchools([]));
  }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setScore = (k, v) => setForm((f) => ({ ...f, scores: { ...f.scores, [k]: v } }));

  const canSave = useMemo(() => isFormComplete(form) && !saving, [form, saving]);

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    const RETRY_TOAST_ID = "save-retry-status";
    try {
      const payload = toApiPayload(form);
      const res = await postWithRetry("/sessions", payload, {
        onRetry: (attempt) => {
          toast.loading(
            attempt === 1
              ? "Server is waking up — hanging on…"
              : `Still waking up — retry ${attempt + 1} of 3…`,
            { id: RETRY_TOAST_ID, duration: 15000 },
          );
        },
      });
      toast.dismiss(RETRY_TOAST_ID);
      setLastResult({ ...res.data, teacher_name: payload.teacher_name });
      setSavedThisSession((n) => n + 1);
      // Keep some ambient fields to make the next submission faster
      setForm((f) => ({
        ...emptyForm(),
        school: f.school === "__other__" ? "" : f.school,
        year_level: f.year_level,
        teacher_name: f.teacher_name,
        role: f.role,
        date: f.date,
        term: f.term,
        programme_type: f.programme_type,
      }));
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success("Session log saved");
    } catch (err) {
      toast.dismiss(RETRY_TOAST_ID);
      const status = err?.response?.status;
      const isNetwork = !err.response;
      const isServer = typeof status === "number" && status >= 500;
      let msg;
      let desc;
      if (isNetwork || isServer) {
        msg = "Server didn't respond after 3 tries";
        desc = "This can happen briefly when the server is waking up. Your entries are still here — tap Save again in ~30 seconds.";
      } else {
        msg = err?.response?.data?.detail || "Couldn't save — please check your entries and try again.";
        desc = "Your entries have not been cleared.";
      }
      toast.error(msg, { duration: 10000, description: desc });
    } finally {
      setSaving(false);
    }
  };

  const handleClear = () => {
    if (!window.confirm("Clear all fields and start a new session log?")) return;
    setForm(emptyForm());
    setLastResult(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] pb-32">
      <BrandHeader
        subtitle="Fill this in during the last five minutes of your session — mobile-friendly, no login."
        right={
          <Link
            to="/admin/login"
            className="text-xs text-neutral-500 hover:text-neutral-800"
            data-testid="admin-link"
          >
            Admin
          </Link>
        }
      />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {lastResult && (
          <div
            data-testid="save-success-banner"
            className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3"
          >
            <CheckCircle2 className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
            <div className="text-sm text-emerald-900">
              <div className="font-semibold">Session saved.</div>
              <div className="text-emerald-800 mt-0.5">
                Engagement <b>{lastResult.engagement_score}/25</b>{" · "}
                Behaviour <b>{lastResult.behaviour_score}/25</b>{" · "}
                Collective Energy <b>{lastResult.collective_energy_score}/25</b>
                {lastResult.cpi_total !== null && lastResult.cpi_total !== undefined ? (
                  <> {" · "}<b>CPI Total {lastResult.cpi_total}/100</b></>
                ) : (
                  <> {" · "}<b>CPI Total: N/A (Invalid Attendance)</b></>
                )}
              </div>
            </div>
          </div>
        )}

        <SessionFormBody
          form={form}
          setField={setField}
          setScore={setScore}
          schools={schools}
        />
      </main>

      {/* Sticky save bar */}
      <div className="fixed bottom-0 inset-x-0 border-t border-neutral-200 bg-white/95 backdrop-blur z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <Button
            variant="outline"
            onClick={handleClear}
            data-testid="btn-clear-form"
            className="h-11"
          >
            Clear form
          </Button>
          <Button
            onClick={handleSave}
            disabled={!canSave}
            data-testid="btn-save-session"
            className="h-11 flex-1 bg-[#2D5A4F] hover:bg-[#264d43] text-white"
          >
            {saving ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving…</>
            ) : (
              "Save Session Log"
            )}
          </Button>
          <div className="text-xs text-neutral-500 text-right leading-tight w-20 shrink-0" data-testid="save-count">
            <b className="text-neutral-900">{savedThisSession}</b> saved
            <br />
            this session
          </div>
        </div>
      </div>
    </div>
  );
}
